import { useState, useCallback } from 'react'
import { Upload, Plus, Trash2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { parsePdfDepartmentTotals } from '../../lib/tillParser'

const CASH_OUT_TYPES = ['Expense', 'Float Change', 'Payout', 'Other']

const emptyRow = () => ({ description: '', amount: '', type: 'Expense' })

async function extractPdfText(file) {
  const { configurePdfWorker, pdfjsLib } = await import('../../lib/pdfWorker')
  configurePdfWorker()
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument(arrayBuffer).promise
  let fullText = ''
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    const items = content.items.filter(item => item.str.trim())
    const rows = []
    let currentRow = []
    let lastY = null
    const sorted = [...items].sort((a, b) => {
      const yDiff = b.transform[5] - a.transform[5]
      if (Math.abs(yDiff) > 3) return yDiff
      return a.transform[4] - b.transform[4]
    })
    for (const item of sorted) {
      const y = Math.round(item.transform[5])
      if (lastY !== null && Math.abs(y - lastY) > 3) {
        if (currentRow.length > 0) rows.push(currentRow)
        currentRow = []
      }
      currentRow.push(item.str)
      lastY = y
    }
    if (currentRow.length > 0) rows.push(currentRow)
    fullText += rows.map(r => r.join('  ')).join('\n') + '\n'
  }
  return fullText
}

function getVarianceStatus(variance) {
  const abs = Math.abs(variance)
  if (abs <= 50) return { label: 'BALANCED', color: 'bg-green-600 text-white' }
  if (abs <= 200) return { label: 'MINOR VARIANCE', color: 'bg-amber-600 text-white' }
  return { label: 'SHORTAGE', color: 'bg-red-600 text-white' }
}

export default function DailyEntry({ sales, setSales, cashOutItems, setCashOutItems, actualCash, setActualCash, onSave, existingRecord }) {
  const [pdfStatus, setPdfStatus] = useState('')
  const [dragging, setDragging] = useState(false)

  const handlePdfFile = useCallback(async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      setPdfStatus('Please upload a PDF file')
      return
    }
    setPdfStatus('Parsing PDF...')
    try {
      const text = await extractPdfText(file)
      const totals = parsePdfDepartmentTotals(text)
      setSales({
        kitchen: totals.kitchen || 0,
        bakery: totals.bakery || 0,
        tuckShop: totals.tuckShop || 0,
        grandTotal: totals.grandTotal || 0,
      })
      setPdfStatus('PDF parsed successfully')
    } catch (err) {
      setPdfStatus(`Error: ${err.message}`)
    }
  }, [setSales])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    handlePdfFile(file)
  }, [handlePdfFile])

  const handleFileInput = (e) => {
    const file = e.target.files[0]
    if (file) handlePdfFile(file)
  }

  const totalCashOut = cashOutItems.reduce((s, r) => s + (Number(r.amount) || 0), 0)
  const totalSales = Number(sales.grandTotal) || 0
  const expectedTill = totalSales - totalCashOut
  const variance = expectedTill - (Number(actualCash) || 0)
  const departmentSum = (Number(sales.kitchen) || 0) + (Number(sales.bakery) || 0) + (Number(sales.tuckShop) || 0)
  const sumMismatch = totalSales > 0 && departmentSum > 0 && Math.abs(departmentSum - totalSales) > 1
  const status = getVarianceStatus(variance)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: POS PDF Upload + Sales */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-300">POS Sales Data</h3>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
              dragging ? 'border-brand-500 bg-brand-950/20' : 'border-gray-700 hover:border-gray-600'
            }`}
            onClick={() => document.getElementById('till-pdf-input')?.click()}
          >
            <Upload size={24} className="mx-auto mb-2 text-gray-500" />
            <p className="text-sm text-gray-400">Drop POS PDF here or click to upload</p>
            <p className="text-xs text-gray-600 mt-1">Extracts Kitchen, Bakery, and Tuck Shop totals</p>
            <input
              id="till-pdf-input"
              type="file"
              accept=".pdf,application/pdf,application/x-pdf"
              onChange={handleFileInput}
              style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
            />
          </div>
          {pdfStatus && (
            <p className={`text-xs ${pdfStatus.startsWith('Error') ? 'text-red-400' : 'text-green-400'}`}>
              {pdfStatus}
            </p>
          )}

          {/* Editable sales fields */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'kitchen', label: 'Kitchen Sales' },
                { key: 'bakery', label: 'Bakery Sales' },
                { key: 'tuckShop', label: 'Tuck Shop Sales' },
                { key: 'grandTotal', label: 'Grand Total' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="text-xs text-gray-400 block mb-1">{label}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">R</span>
                    <input
                      type="number"
                      step="0.01"
                      value={sales[key] || ''}
                      onChange={e => setSales(prev => ({ ...prev, [key]: Number(e.target.value) || 0 }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 pl-7 text-sm text-white outline-none focus:border-brand-500"
                    />
                  </div>
                </div>
              ))}
            </div>
            {sumMismatch && (
              <div className="flex items-center gap-2 bg-amber-950/50 border border-amber-800 rounded p-2">
                <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                <span className="text-xs text-amber-300">
                  Department sum (R{departmentSum.toFixed(2)}) does not match grand total (R{totalSales.toFixed(2)})
                </span>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Cash Out */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-300">Cash Out Line Items</h3>
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
            {/* Header */}
            <div className="grid grid-cols-[1fr_100px_120px_32px] gap-2 text-xs text-gray-500 font-medium">
              <span>Description</span>
              <span>Amount</span>
              <span>Type</span>
              <span></span>
            </div>
            {/* Rows */}
            {cashOutItems.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_100px_120px_32px] gap-2 items-center">
                <input
                  type="text"
                  value={row.description}
                  onChange={e => {
                    const next = [...cashOutItems]
                    next[i] = { ...next[i], description: e.target.value }
                    setCashOutItems(next)
                  }}
                  placeholder="e.g. Electricity"
                  className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-brand-500"
                />
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">R</span>
                  <input
                    type="number"
                    step="0.01"
                    value={row.amount}
                    onChange={e => {
                      const next = [...cashOutItems]
                      next[i] = { ...next[i], amount: e.target.value }
                      setCashOutItems(next)
                    }}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 pl-5 text-sm text-white outline-none focus:border-brand-500"
                  />
                </div>
                <select
                  value={row.type}
                  onChange={e => {
                    const next = [...cashOutItems]
                    next[i] = { ...next[i], type: e.target.value }
                    setCashOutItems(next)
                  }}
                  className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-brand-500"
                >
                  {CASH_OUT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button
                  onClick={() => setCashOutItems(cashOutItems.filter((_, j) => j !== i))}
                  className="text-gray-500 hover:text-red-400 p-1"
                  title="Remove row"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setCashOutItems([...cashOutItems, emptyRow()])}
                className="flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300"
              >
                <Plus size={14} /> Add Row
              </button>
              <span className="text-sm font-medium text-white">Total: R{totalCashOut.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM: Reconciliation Summary */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Reconciliation Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <p className="text-xs text-gray-500">Total POS Sales</p>
            <p className="text-lg font-bold text-white">R{totalSales.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Less Cash Out</p>
            <p className="text-lg font-bold text-red-400">-R{totalCashOut.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Expected Till Balance</p>
            <p className="text-lg font-bold text-brand-400">R{expectedTill.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Actual Cash Count</p>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">R</span>
              <input
                type="number"
                step="0.01"
                value={actualCash}
                onChange={e => setActualCash(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 pl-7 text-sm text-white outline-none focus:border-brand-500"
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500">Variance</p>
            <p className={`text-lg font-bold ${variance === 0 ? 'text-green-400' : variance > 0 ? 'text-amber-400' : 'text-red-400'}`}>
              {variance >= 0 ? '' : '-'}R{Math.abs(variance).toFixed(2)}
            </p>
            {actualCash !== '' && (
              <span className={`inline-block text-xs px-2 py-0.5 rounded mt-1 ${status.color}`}>
                {status.label}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={onSave}
          className="bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors"
        >
          {existingRecord ? 'Update Till Record' : 'Save Till Record'}
        </button>
      </div>
    </div>
  )
}
